import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { 
  ChevronRight, 
  ChevronLeft, 
  Plus, 
  Trash2, 
  Save,
  FileText,
  Users,
  Target,
  CheckSquare,
  AlertCircle,
  Layers,
  Clock,
  Link,
  GripVertical,
  Settings,
  Eye
} from 'lucide-react';
import { PriorityBadge } from '../ui/StatusBadge';

const SopBuilderWizard = ({ onClose }) => {
  const { currentUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState({});
  
  const steps = [
    { id: 'basic', title: 'מידע בסיסי', icon: FileText },
    { id: 'phases', title: 'שלבים', icon: Layers },
    { id: 'tasks', title: 'משימות ותנאים', icon: CheckSquare },
    { id: 'resources', title: 'משאבים', icon: Link },
    { id: 'metrics', title: 'מדדי הצלחה', icon: Target },
    { id: 'preview', title: 'תצוגה מקדימה', icon: Eye }
  ];

  // Template state
  const [template, setTemplate] = useState({
    name: '',
    description: '',
    category: 'onboarding',
    type: 'sop',
    defaultDurationDays: 30,
    isActive: true,
    version: 1,
    tags: [],
    phases: [{
      id: `ph_${Date.now()}`,
      name: 'שלב 1',
      order: 1,
      goals: [],
      resources: [],
      tasks: []
    }],
    successMetrics: []
  });

  // Validate current step
  const validateStep = () => {
    const newErrors = {};
    
    switch (currentStep) {
      case 0: // Basic Info
        if (!template.name.trim()) newErrors.name = 'שם התבנית חובה';
        if (!template.description.trim()) newErrors.description = 'תיאור חובה';
        break;
      case 1: // Phases
        if (template.phases.length === 0) newErrors.phases = 'חובה להגדיר לפחות שלב אחד';
        template.phases.forEach((phase, i) => {
          if (!phase.name.trim()) newErrors[`phase_${i}_name`] = 'שם השלב חובה';
        });
        break;
      case 2: // Tasks
        let hasAnyTask = false;
        template.phases.forEach(phase => {
          if (phase.tasks.length > 0) hasAnyTask = true;
        });
        if (!hasAnyTask) newErrors.tasks = 'חובה להגדיר לפחות משימה אחת';
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation
  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(Math.min(currentStep + 1, steps.length - 1));
    }
  };

  const handleBack = () => {
    setCurrentStep(Math.max(currentStep - 1, 0));
  };

  // Save template
  const handleSave = async () => {
    if (!validateStep()) return;
    
    // Additional validation - use fallback if orgId is missing
    let orgId = currentUser?.orgId;
    if (!orgId) {
      console.warn('Missing orgId, using fallback. Current user:', currentUser);
      // Use a fallback orgId - in production this should be handled properly
      orgId = currentUser?.id || 'default-org';
      console.log('Using fallback orgId:', orgId);
    }
    
    if (!template.name?.trim()) {
      alert('שגיאה: שם התבנית חובה');
      return;
    }
    
    try {
      const templateData = {
        ...template,
        orgId: orgId,
        createdBy: currentUser.id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      console.log('Saving template data:', templateData);
      console.log('Current user:', currentUser);
      
      const docRef = await addDoc(collection(db, 'sopTemplates'), templateData);
      console.log('Template saved with ID:', docRef.id);
      
      alert('תבנית ה-SOP נשמרה בהצלחה!');
      onClose();
    } catch (error) {
      console.error('Error saving template:', error);
      console.error('Error details:', error.message, error.code);
      alert('שגיאה בשמירת התבנית: ' + error.message);
    }
  };

  // Phase management
  const addPhase = () => {
    const newPhase = {
      id: `ph_${Date.now()}`,
      name: `שלב ${template.phases.length + 1}`,
      order: template.phases.length + 1,
      goals: [],
      resources: [],
      tasks: []
    };
    setTemplate({ ...template, phases: [...template.phases, newPhase] });
  };

  const updatePhase = (phaseId, updates) => {
    setTemplate({
      ...template,
      phases: template.phases.map(phase =>
        phase.id === phaseId ? { ...phase, ...updates } : phase
      )
    });
  };

  const deletePhase = (phaseId) => {
    if (template.phases.length <= 1) {
      alert('חובה להשאיר לפחות שלב אחד');
      return;
    }
    setTemplate({
      ...template,
      phases: template.phases.filter(p => p.id !== phaseId)
    });
  };

  // Task management
  const addTask = (phaseId) => {
    const newTask = {
      id: `tt_${Date.now()}`,
      title: '',
      description: '',
      relativeStartDay: 0,
      durationDays: 1,
      assigneeRole: 'target',
      priority: 'medium',
      checklist: [],
      successCriteria: { type: 'checkbox_all' },
      conditions: [],
      resources: []
    };
    
    setTemplate({
      ...template,
      phases: template.phases.map(phase =>
        phase.id === phaseId
          ? { ...phase, tasks: [...phase.tasks, newTask] }
          : phase
      )
    });
  };

  const updateTask = (phaseId, taskId, updates) => {
    setTemplate({
      ...template,
      phases: template.phases.map(phase =>
        phase.id === phaseId
          ? {
              ...phase,
              tasks: phase.tasks.map(task =>
                task.id === taskId ? { ...task, ...updates } : task
              )
            }
          : phase
      )
    });
  };

  const deleteTask = (phaseId, taskId) => {
    setTemplate({
      ...template,
      phases: template.phases.map(phase =>
        phase.id === phaseId
          ? { ...phase, tasks: phase.tasks.filter(t => t.id !== taskId) }
          : phase
      )
    });
  };

  // Success metrics management
  const addMetric = (metric) => {
    setTemplate({
      ...template,
      successMetrics: [...template.successMetrics, metric]
    });
  };

  const removeMetric = (index) => {
    setTemplate({
      ...template,
      successMetrics: template.successMetrics.filter((_, i) => i !== index)
    });
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Basic Info
        return <BasicInfoStep template={template} setTemplate={setTemplate} errors={errors} />;
      case 1: // Phases
        return <PhasesStep 
          template={template} 
          addPhase={addPhase}
          updatePhase={updatePhase}
          deletePhase={deletePhase}
          errors={errors}
        />;
      case 2: // Tasks
        return <TasksStep
          template={template}
          addTask={addTask}
          updateTask={updateTask}
          deleteTask={deleteTask}
          errors={errors}
        />;
      case 3: // Resources
        return <ResourcesStep template={template} setTemplate={setTemplate} />;
      case 4: // Success Metrics
        return <MetricsStep
          template={template}
          addMetric={addMetric}
          removeMetric={removeMetric}
        />;
      case 5: // Preview
        return <PreviewStep template={template} />;
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full max-w-5xl max-h-[95vh] flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-t-lg flex-shrink-0">
            <h2 className="text-2xl font-bold mb-2">אשף בניית תבנית SOP</h2>
            <p className="text-purple-100">צור תהליך עבודה סטנדרטי חדש לארגון</p>
          </div>

          {/* Progress Bar */}
          <div className="bg-gray-100 p-4 flex-shrink-0">
            <div className="flex justify-between items-center">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={step.id} className="flex-1">
                    <div className="flex items-center">
                      <div
                        className={`
                          flex items-center justify-center w-10 h-10 rounded-full
                          ${index === currentStep
                            ? 'bg-blue-600 text-white'
                            : index < currentStep
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-300 text-gray-600'}
                        `}
                      >
                        <Icon size={20} />
                      </div>
                      <div className="flex-1 ml-2">
                        <p className={`text-sm ${index === currentStep ? 'font-semibold' : ''}`}>
                          {step.title}
                        </p>
                      </div>
                      {index < steps.length - 1 && (
                        <div
                          className={`
                            h-1 flex-1 mx-2
                            ${index < currentStep ? 'bg-green-500' : 'bg-gray-300'}
                          `}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(95vh - 240px)' }}>
            {renderStepContent()}
          </div>

          {/* Footer */}
          <div className="border-t p-6 flex justify-between flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              ביטול
            </button>
            
            <div className="flex gap-2">
              {currentStep > 0 && (
                <button
                  onClick={handleBack}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2"
                >
                  <ChevronRight size={18} />
                  הקודם
                </button>
              )}
              
              {currentStep < steps.length - 1 ? (
                <button
                  onClick={handleNext}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  הבא
                  <ChevronLeft size={18} />
                </button>
              ) : (
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Save size={18} />
                  שמור תבנית
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step Components

const BasicInfoStep = ({ template, setTemplate, errors }) => {
  const categories = [
    { id: 'onboarding', label: 'קליטת עובד' },
    { id: 'role_training', label: 'הכשרת תפקיד' },
    { id: 'department_ops', label: 'תהליכי מחלקה' },
    { id: 'management', label: 'ניהול ובקרה' }
  ];

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">
          שם התבנית <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={template.name}
          onChange={(e) => setTemplate({ ...template, name: e.target.value })}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
            errors.name ? 'border-red-500' : ''
          }`}
          placeholder="לדוגמה: קליטת עובד חדש - מכירות"
        />
        {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          תיאור <span className="text-red-500">*</span>
        </label>
        <textarea
          value={template.description}
          onChange={(e) => setTemplate({ ...template, description: e.target.value })}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
            errors.description ? 'border-red-500' : ''
          }`}
          rows="3"
          placeholder="תאר את מטרת התהליך ולמי הוא מיועד"
        />
        {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">קטגוריה</label>
          <select
            value={template.category}
            onChange={(e) => setTemplate({ ...template, category: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">משך כולל (ימים)</label>
          <input
            type="number"
            value={template.defaultDurationDays}
            onChange={(e) => setTemplate({ ...template, defaultDurationDays: parseInt(e.target.value) || 30 })}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            min="1"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">תגיות</label>
        <input
          type="text"
          value={template.tagsText || template.tags.join(' ')}
          onChange={(e) => setTemplate({ 
            ...template, 
            tagsText: e.target.value
          })}
          onBlur={(e) => setTemplate({
            ...template,
            tags: e.target.value.split(/[\s,]+/).map(t => t.trim()).filter(Boolean),
            tagsText: undefined
          })}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="למשל: מכירות קליטה עובד חדש"
        />
        <p className="text-xs text-gray-500 mt-1">כתוב תגיות עם רווחים או פסיקים</p>
      </div>
    </div>
  );
};

const PhasesStep = ({ template, addPhase, updatePhase, deletePhase, errors }) => {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">שלבי התהליך</h3>
        <button
          onClick={addPhase}
          className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center gap-1"
        >
          <Plus size={16} />
          הוסף שלב
        </button>
      </div>

      {errors.phases && (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
          <AlertCircle size={16} className="inline mr-1" />
          {errors.phases}
        </div>
      )}

      <div className="space-y-4">
        {template.phases.map((phase, index) => (
          <div key={phase.id} className="border rounded-lg p-4">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2 flex-1">
                <GripVertical size={20} className="text-gray-400" />
                <input
                  type="text"
                  value={phase.name}
                  onChange={(e) => updatePhase(phase.id, { name: e.target.value })}
                  className={`flex-1 text-lg font-medium bg-transparent border-b border-gray-300 focus:border-blue-500 outline-none ${
                    errors[`phase_${index}_name`] ? 'border-red-500' : ''
                  }`}
                  placeholder="שם השלב"
                />
              </div>
              {template.phases.length > 1 && (
                <button
                  onClick={() => deletePhase(phase.id)}
                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">יעדי השלב</label>
              <textarea
                value={phase.goalsText || phase.goals.join('\n')}
                onChange={(e) => updatePhase(phase.id, {
                  goalsText: e.target.value
                })}
                onBlur={(e) => updatePhase(phase.id, {
                  goals: e.target.value.split('\n').map(g => g.trim()).filter(Boolean),
                  goalsText: undefined
                })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="כתוב יעדים עם רווחים, כל יעד בשורה נפרדת..."
                rows="3"
              />
              <p className="text-xs text-gray-500 mt-1">כתוב בחופשיות - כל יעד בשורה נפרדת</p>
            </div>

            <div className="mt-3 text-sm text-gray-600">
              <span>משימות בשלב: {phase.tasks.length}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TasksStep = ({ template, addTask, updateTask, deleteTask, errors }) => {
  const [selectedPhase, setSelectedPhase] = useState(template.phases[0]?.id || '');
  const [showConditionBuilder, setShowConditionBuilder] = useState(null);

  if (!selectedPhase && template.phases.length > 0) {
    setSelectedPhase(template.phases[0].id);
  }

  const currentPhase = template.phases.find(p => p.id === selectedPhase);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold">משימות ותנאים</h3>
          <select
            value={selectedPhase}
            onChange={(e) => setSelectedPhase(e.target.value)}
            className="px-3 py-1 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {template.phases.map(phase => (
              <option key={phase.id} value={phase.id}>{phase.name}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => addTask(selectedPhase)}
          className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center gap-1"
        >
          <Plus size={16} />
          הוסף משימה
        </button>
      </div>

      {errors.tasks && (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
          <AlertCircle size={16} className="inline mr-1" />
          {errors.tasks}
        </div>
      )}

      {currentPhase && (
        <div className="space-y-3">
          {currentPhase.tasks.map((task, index) => (
            <div key={task.id} className="border rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                <input
                  type="text"
                  value={task.title}
                  onChange={(e) => updateTask(selectedPhase, task.id, { title: e.target.value })}
                  className="flex-1 font-medium bg-transparent border-b border-gray-300 focus:border-blue-500 outline-none"
                  placeholder="כותרת המשימה"
                />
                <button
                  onClick={() => deleteTask(selectedPhase, task.id)}
                  className="p-1 text-red-600 hover:bg-red-50 rounded ml-2"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">יום התחלה</label>
                  <input
                    type="number"
                    value={task.relativeStartDay}
                    onChange={(e) => updateTask(selectedPhase, task.id, { 
                      relativeStartDay: parseInt(e.target.value) || 0 
                    })}
                    className="w-full px-2 py-1 border rounded text-sm"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">משך (ימים)</label>
                  <input
                    type="number"
                    value={task.durationDays}
                    onChange={(e) => updateTask(selectedPhase, task.id, { 
                      durationDays: parseInt(e.target.value) || 1 
                    })}
                    className="w-full px-2 py-1 border rounded text-sm"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">עדיפות</label>
                  <select
                    value={task.priority}
                    onChange={(e) => updateTask(selectedPhase, task.id, { priority: e.target.value })}
                    className="w-full px-2 py-1 border rounded text-sm"
                  >
                    <option value="low">נמוכה</option>
                    <option value="medium">בינונית</option>
                    <option value="high">גבוהה</option>
                    <option value="urgent">דחוף</option>
                  </select>
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-sm text-gray-600 mb-1">הקצאה ל</label>
                <select
                  value={task.assigneeRole}
                  onChange={(e) => updateTask(selectedPhase, task.id, { assigneeRole: e.target.value })}
                  className="w-full px-2 py-1 border rounded text-sm"
                >
                  <option value="target">יעד התהליך</option>
                  <option value="manager">מנהל ישיר</option>
                  <option value="it">מחלקת IT</option>
                  <option value="self">מפעיל התהליך</option>
                </select>
              </div>

              <div className="mb-3">
                <label className="block text-sm text-gray-600 mb-1">תיאור</label>
                <textarea
                  value={task.description}
                  onChange={(e) => updateTask(selectedPhase, task.id, { description: e.target.value })}
                  className="w-full px-2 py-1 border rounded text-sm"
                  rows="2"
                  placeholder="פרט על המשימה ומה צריך לעשות"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setShowConditionBuilder(task.id)}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200 flex items-center gap-1"
                >
                  <Settings size={14} />
                  תנאים ({task.conditions.length})
                </button>
                <button
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200 flex items-center gap-1"
                >
                  <CheckSquare size={14} />
                  רשימת בדיקה ({task.checklist.length})
                </button>
              </div>

              {/* Condition Builder Modal */}
              {showConditionBuilder === task.id && (
                <ConditionBuilder
                  task={task}
                  onUpdate={(conditions) => {
                    updateTask(selectedPhase, task.id, { conditions });
                    setShowConditionBuilder(null);
                  }}
                  onClose={() => setShowConditionBuilder(null)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ResourcesStep = ({ template, setTemplate }) => {
  const [selectedResources, setSelectedResources] = useState([]);
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">משאבים ומסמכים</h3>
      
      <div className="bg-blue-50 p-4 rounded-lg">
        <p className="text-sm text-blue-800">
          <AlertCircle size={16} className="inline mr-1" />
          בחר משאבים קיימים מהמאגר או צור חדשים בדף ניהול המשאבים
        </p>
      </div>

      <div className="space-y-2">
        <p className="text-sm text-gray-600">תכונה זו תהיה זמינה לאחר יצירת מערכת ניהול המשאבים</p>
      </div>
    </div>
  );
};

const MetricsStep = ({ template, addMetric, removeMetric }) => {
  const [newMetric, setNewMetric] = useState({
    kpi: '',
    target: 0,
    unit: 'ratio'
  });

  const predefinedMetrics = [
    { kpi: 'onboarding_completion', label: 'אחוז השלמת התהליך', target: 90, unit: 'ratio' },
    { kpi: 'time_to_productivity', label: 'זמן עד פרודוקטיביות', target: 30, unit: 'days' },
    { kpi: 'phase_sla_adherence', label: 'עמידה ב-SLA של שלבים', target: 85, unit: 'ratio' },
    { kpi: 'task_overdue_rate', label: 'שיעור משימות באיחור', target: 10, unit: 'ratio' }
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">מדדי הצלחה (KPIs)</h3>
      
      <div className="space-y-3">
        {predefinedMetrics.map((metric) => {
          const isSelected = template.successMetrics.some(m => m.kpi === metric.kpi);
          return (
            <div key={metric.kpi} className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    if (e.target.checked) {
                      addMetric(metric);
                    } else {
                      const index = template.successMetrics.findIndex(m => m.kpi === metric.kpi);
                      if (index >= 0) removeMetric(index);
                    }
                  }}
                  className="w-4 h-4"
                />
                <div>
                  <p className="font-medium">{metric.label}</p>
                  <p className="text-sm text-gray-600">
                    יעד: {metric.target}{metric.unit === 'ratio' ? '%' : ` ${metric.unit}`}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium mb-2">הוסף מדד מותאם אישית</h4>
        <div className="grid grid-cols-3 gap-3">
          <input
            type="text"
            value={newMetric.kpi}
            onChange={(e) => setNewMetric({...newMetric, kpi: e.target.value})}
            placeholder="שם המדד"
            className="px-3 py-2 border rounded-lg"
          />
          <input
            type="number"
            value={newMetric.target}
            onChange={(e) => setNewMetric({...newMetric, target: parseInt(e.target.value) || 0})}
            placeholder="יעד"
            className="px-3 py-2 border rounded-lg"
          />
          <select
            value={newMetric.unit}
            onChange={(e) => setNewMetric({...newMetric, unit: e.target.value})}
            className="px-3 py-2 border rounded-lg"
          >
            <option value="ratio">אחוז</option>
            <option value="days">ימים</option>
            <option value="count">מספר</option>
          </select>
        </div>
      </div>
    </div>
  );
};

const PreviewStep = ({ template }) => {
  const totalTasks = template.phases.reduce((sum, phase) => sum + phase.tasks.length, 0);
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">תצוגה מקדימה</h3>
      
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-lg mb-2">{template.name}</h4>
        <p className="text-gray-600">{template.description}</p>
        
        <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
          <div>
            <span className="text-gray-500">קטגוריה:</span>
            <span className="font-medium mr-2">{template.category}</span>
          </div>
          <div>
            <span className="text-gray-500">משך:</span>
            <span className="font-medium mr-2">{template.defaultDurationDays} ימים</span>
          </div>
          <div>
            <span className="text-gray-500">משימות:</span>
            <span className="font-medium mr-2">{totalTasks}</span>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-medium mb-2">שלבים ומשימות</h4>
        <div className="space-y-3">
          {template.phases.map((phase, phaseIndex) => (
            <div key={phase.id} className="border rounded-lg p-3">
              <h5 className="font-medium">{phase.name}</h5>
              {phase.goals.length > 0 && (
                <p className="text-sm text-gray-600 mt-1">יעדים: {phase.goals.join(', ')}</p>
              )}
              <div className="mt-2 space-y-1">
                {phase.tasks.map((task) => (
                  <div key={task.id} className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">•</span>
                    <span>{task.title}</span>
                    <PriorityBadge priority={task.priority} size="xs" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {template.successMetrics.length > 0 && (
        <div>
          <h4 className="font-medium mb-2">מדדי הצלחה</h4>
          <div className="space-y-1">
            {template.successMetrics.map((metric, i) => (
              <div key={i} className="text-sm">
                <span className="font-medium">{metric.kpi}:</span>
                <span className="mr-2 text-gray-600">
                  יעד {metric.target}{metric.unit === 'ratio' ? '%' : ` ${metric.unit}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Condition Builder Component
const ConditionBuilder = ({ task, onUpdate, onClose }) => {
  const [conditions, setConditions] = useState(task.conditions || []);
  
  const addCondition = () => {
    setConditions([...conditions, {
      if: { eq: [{ var: 'department' }, ''] },
      then: { include: true }
    }]);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <h3 className="text-lg font-semibold mb-4">הגדרת תנאים למשימה</h3>
        
        <div className="space-y-3">
          {conditions.map((condition, index) => (
            <div key={index} className="border rounded-lg p-3">
              <p className="text-sm font-medium mb-2">תנאי {index + 1}</p>
              <div className="text-sm">
                <select className="px-2 py-1 border rounded">
                  <option>אם מחלקה</option>
                  <option>אם תפקיד</option>
                  <option>אם וותק</option>
                </select>
                <span className="mx-2">=</span>
                <input type="text" className="px-2 py-1 border rounded" placeholder="ערך" />
                <span className="mx-2">אז</span>
                <select className="px-2 py-1 border rounded">
                  <option>כלול משימה</option>
                  <option>אל תכלול משימה</option>
                </select>
              </div>
            </div>
          ))}
          
          <button
            onClick={addCondition}
            className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
          >
            הוסף תנאי
          </button>
        </div>

        <div className="mt-6 flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            ביטול
          </button>
          <button
            onClick={() => onUpdate(conditions)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            שמור
          </button>
        </div>
      </div>
    </div>
  );
};

export default SopBuilderWizard;